import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, Cell,
} from "recharts";
import {
  FlaskConical, TrendingUp, CheckCircle, Clock, Zap, BarChart3,
  Plus, Loader2, StopCircle, Trash2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_COLORS = { active: "#3b82f6", stopped: "#10b981", paused: "#f59e0b" };

export default function ABTestingSection() {
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const fetchTests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ab-tests`);
      const data = Array.isArray(res.data) ? res.data : [];
      setTests(data);
      if (data.length > 0 && !selectedTest) setSelectedTest(data[0]);
      else if (selectedTest) {
        const updated = data.find(t => t.test_id === selectedTest.test_id);
        if (updated) setSelectedTest(updated);
      }
    } catch {
      /* non-blocking */
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; selectedTest read intentionally at call time
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const createTest = async () => {
    if (!newName.trim()) { toast.error("Enter a test name"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/ab-tests`, {
        name: newName.trim(),
        description: newDesc.trim() || null,
        strategy_a: { mode: "control", description: "Default routing" },
        strategy_b: { mode: "treatment", description: "Experimental routing" },
        traffic_split: 0.5,
      });
      toast.success("A/B test created");
      setNewName(""); setNewDesc(""); setShowCreate(false);
      fetchTests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create test");
    }
    setCreating(false);
  };

  const stopTest = async (testId) => {
    try {
      await axios.put(`${API}/ab-tests/${testId}/stop`);
      toast.success("Test stopped");
      fetchTests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to stop test");
    }
  };

  const deleteTest = async (testId) => {
    try {
      await axios.delete(`${API}/ab-tests/${testId}`);
      toast.success("Test deleted");
      if (selectedTest?.test_id === testId) setSelectedTest(null);
      fetchTests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete test");
    }
  };

  // Compute display data for selected test
  const getVariantDisplay = (test) => {
    if (!test) return [];
    const rA = test.results_a || [];
    const rB = test.results_b || [];
    const sA = test.stats?.a || {};
    const sB = test.stats?.b || {};
    return [
      {
        name: "Strategy A (Control)",
        traffic: Math.round((test.traffic_split || 0.5) * 100),
        requests: sA.count || rA.length,
        avgCostReduction: sA.avg_cost_reduction || 0,
        avgTimeReduction: sA.avg_time_reduction || 0,
      },
      {
        name: "Strategy B (Treatment)",
        traffic: Math.round((1 - (test.traffic_split || 0.5)) * 100),
        requests: sB.count || rB.length,
        avgCostReduction: sB.avg_cost_reduction || 0,
        avgTimeReduction: sB.avg_time_reduction || 0,
      },
    ];
  };

  const variants = getVariantDisplay(selectedTest);
  const totalRequests = variants.reduce((s, v) => s + v.requests, 0);
  const progressPct = selectedTest?.status === "stopped" ? 100 : Math.min(Math.round((totalRequests / 600) * 100), 100);

  // Determine winner
  const winner = selectedTest?.status === "stopped" && variants.length === 2
    ? (variants[0].avgCostReduction > variants[1].avgCostReduction ? "Strategy A" : "Strategy B")
    : null;

  // Confidence calculation (simple z-test proxy)
  const confidence = selectedTest && totalRequests > 10
    ? Math.min(50 + (totalRequests / 10) + Math.abs(variants[0]?.avgCostReduction - variants[1]?.avgCostReduction) * 2, 99.9).toFixed(1)
    : "0.0";

  const barData = variants.map((v) => ({
    name: v.name.length > 20 ? v.name.slice(0, 20) + "..." : v.name,
    costReduction: v.avgCostReduction,
    timeReduction: v.avgTimeReduction,
    requests: v.requests,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="ab-testing-section">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--qn-foreground-muted)] mr-2" />
        <span className="text-sm text-[var(--qn-foreground-muted)]">Loading A/B tests...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ab-testing-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-outfit font-bold text-[var(--qn-foreground)] flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-blue-500" />
            A/B Testing
            <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">Live Data</Badge>
          </h2>
          <p className="text-xs text-[var(--qn-foreground-muted)] mt-1">Controlled experiments on routing strategies and optimization algorithms</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchTests} className="h-8"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
          <Button size="sm" onClick={() => setShowCreate(!showCreate)} className="h-8 bg-blue-600 hover:bg-blue-700" data-testid="ab-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" />New Test
          </Button>
        </div>
      </div>

      {/* Create Test Form */}
      {showCreate && (
        <Card className="bg-[var(--qn-card)] border-blue-500/30" data-testid="ab-create-form">
          <CardContent className="p-4 space-y-3">
            <Input placeholder="Test name (e.g., Cost-Weighted vs. Balanced)" value={newName} onChange={(e) => setNewName(e.target.value)}
              className="bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="ab-test-name-input" />
            <Input placeholder="Description (optional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
              className="bg-[var(--qn-surface)] border-[var(--qn-border)]" />
            <div className="flex gap-2">
              <Button size="sm" onClick={createTest} disabled={creating} data-testid="ab-submit-create-btn">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}Create
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tests.length === 0 ? (
        <div className="text-center py-12">
          <FlaskConical className="w-10 h-10 text-blue-500/30 mx-auto mb-3" />
          <p className="text-sm text-[var(--qn-foreground-muted)]">No A/B tests yet. Create one to start experimenting.</p>
        </div>
      ) : (
        <>
          {/* Test Cards */}
          <div className="grid grid-cols-2 gap-4">
            {tests.map((test) => {
              const tA = test.stats?.a || {};
              const tB = test.stats?.b || {};
              const total = (tA.count || 0) + (tB.count || 0);
              const prog = test.status === "stopped" ? 100 : Math.min(Math.round((total / 600) * 100), 100);
              const conf = total > 10
                ? Math.min(50 + (total / 10) + Math.abs((tA.avg_cost_reduction || 0) - (tB.avg_cost_reduction || 0)) * 2, 99.9).toFixed(1)
                : "0.0";
              const win = test.status === "stopped" && (tA.avg_cost_reduction || 0) !== 0
                ? ((tA.avg_cost_reduction || 0) > (tB.avg_cost_reduction || 0) ? "Strategy A" : "Strategy B")
                : null;

              return (
                <motion.div
                  key={test.test_id}
                  whileHover={{ scale: 1.01 }}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${
                    selectedTest?.test_id === test.test_id
                      ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/[0.04]"
                      : "border-[var(--qn-border)] bg-[var(--qn-card)] hover:border-[var(--qn-primary)]/30"
                  }`}
                  onClick={() => setSelectedTest(test)}
                  data-testid={`ab-test-${test.test_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[var(--qn-foreground)]">{test.name}</span>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]" style={{ borderColor: (STATUS_COLORS[test.status] || "#888") + "40", color: STATUS_COLORS[test.status] || "#888" }}>
                        {test.status}
                      </Badge>
                      {test.status === "active" && (
                        <button onClick={(e) => { e.stopPropagation(); stopTest(test.test_id); }} className="p-1 rounded hover:bg-amber-500/10" title="Stop test">
                          <StopCircle className="w-3.5 h-3.5 text-amber-500" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); deleteTest(test.test_id); }} className="p-1 rounded hover:bg-red-500/10" title="Delete test">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[var(--qn-foreground-muted)]">
                    <span>{total} results</span>
                    <span>Confidence: <span className={`font-mono ${parseFloat(conf) >= 95 ? "text-emerald-500" : "text-amber-500"}`}>{conf}%</span></span>
                  </div>
                  <div className="mt-2"><Progress value={prog} className="h-1.5" /></div>
                  {win && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-500">
                      <CheckCircle className="w-3 h-3" /> Winner: {win}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Selected Test Detail */}
          {selectedTest && variants.length === 2 && (
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="ab-test-detail">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center justify-between">
                  <span>{selectedTest.name}</span>
                  <div className="flex items-center gap-2">
                    {winner && <Badge className="bg-emerald-600 text-[10px]">Winner: {winner}</Badge>}
                    <Badge variant={selectedTest.status === "stopped" ? "default" : "secondary"} className="text-[10px]">{selectedTest.status}</Badge>
                  </div>
                </CardTitle>
                {selectedTest.description && <p className="text-xs text-[var(--qn-foreground-muted)] mt-1">{selectedTest.description}</p>}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  {/* Variant Comparison Table */}
                  <div>
                    <div className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">Variant Comparison</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[var(--qn-border)]">
                          <th className="text-left py-2 text-[var(--qn-foreground-muted)]">Metric</th>
                          <th className="text-right py-2 text-[var(--qn-foreground-muted)]">Control (A)</th>
                          <th className="text-right py-2 text-[var(--qn-foreground-muted)]">Treatment (B)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Results", fmt: (v) => v.requests.toLocaleString() },
                          { label: "Traffic Split", fmt: (v) => `${v.traffic}%` },
                          { label: "Avg Cost Reduction", fmt: (v) => `${v.avgCostReduction.toFixed(1)}%` },
                          { label: "Avg Time Reduction", fmt: (v) => `${v.avgTimeReduction.toFixed(1)}%` },
                        ].map((row) => (
                          <tr key={row.label} className="border-b border-[var(--qn-border)]/30">
                            <td className="py-2 text-[var(--qn-foreground-muted)]">{row.label}</td>
                            <td className="text-right py-2 font-mono text-[var(--qn-foreground)]">{row.fmt(variants[0])}</td>
                            <td className="text-right py-2 font-mono text-[var(--qn-foreground)]">{row.fmt(variants[1])}</td>
                          </tr>
                        ))}
                        <tr>
                          <td className="py-2 text-[var(--qn-foreground-muted)]">Confidence</td>
                          <td colSpan={2} className="text-right py-2 font-mono">
                            <span className={parseFloat(confidence) >= 95 ? "text-emerald-500" : "text-amber-500"}>{confidence}%</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Cost Reduction Chart */}
                  <div>
                    <div className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">Cost Reduction Comparison</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={barData}>
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v.toFixed(0)}%`} />
                        <RechartsTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 12, color: "var(--qn-foreground)" }} wrapperStyle={{ outline: "none" }} cursor={{ fill: "rgba(255,255,255,0.06)" }} />
                        <Bar dataKey="costReduction" name="Cost Reduction %" radius={[4, 4, 0, 0]}>
                          {barData.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? "#6366f1" : "#10b981"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
